import { Injectable } from '@angular/core';
import { CustomFormData } from '../ngrx/orderform.models';

interface OrderFormProgressData {
  shipping_address?: CustomFormData;
  billing_address?: CustomFormData;
  has_billing_address?: number;
  savedAt?: number;
}

@Injectable({
  providedIn: 'root',
})
export class OrderformLocalStorageService {
  private readonly STORAGE_KEY = 'ds24_orderform_progress';
  private readonly EXPIRY_DAYS = 7;
  
  /**
   * Feature flag to enable/disable localStorage saving
   * Set from app.module.ts based on GrowthBook feature flag
   */
  private _enabled = false;
  
  /**
   * List of fields that should NOT be saved (payment information and sensitive data)
   */
  private readonly EXCLUDED_FIELDS = [
    'cardNumber',
    'cardName',
    'expiryDate',
    'cvv',
    'cvc',
    'card_number',
    'card_name',
    'expiry_date',
    'account_holder',
    'iban',
    'bic',
    'bank_account',
    'payment_provider_meta',
  ];

  /**
   * Enable or disable localStorage feature
   */
  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    
    // If disabling, clear any existing data
    if (!enabled) {
      this.clearProgress();
    }
  }

  /**
   * Check if feature is enabled
   */
  isEnabled(): boolean {
    return this._enabled;
  }

  /**
   * Save progress to localStorage
   */
  saveProgress(data: OrderFormProgressData): void {
    if (!this._enabled) {
      return;
    }
    
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      const sanitizedData = this.sanitizeData(data);
      const dataToSave: OrderFormProgressData = {
        ...sanitizedData,
        savedAt: new Date().getTime(),
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Failed to save order form progress to localStorage:', error);
    }
  }

  /**
   * Load progress from localStorage
   */
  loadProgress(): OrderFormProgressData | null {
    if (!this._enabled) {
      return null;
    }
    
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    try {
      const savedData = localStorage.getItem(this.STORAGE_KEY);
      if (!savedData) {
        return null;
      }

      const data: OrderFormProgressData = JSON.parse(savedData);

      // Check if data has expired
      if (this.isExpired(data)) {
        this.clearProgress();
        return null;
      }

      // Remove timestamp before returning data
      const { savedAt, ...progressData } = data;
      return progressData;
    } catch (error) {
      console.error('Failed to load order form progress from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear saved progress
   */
  clearProgress(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear order form progress from localStorage:', error);
    }
  }

  /**
   * Check if data has expired
   */
  private isExpired(data: OrderFormProgressData): boolean {
    if (!data.savedAt) {
      return true;
    }

    const expiryTime = this.EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    const now = new Date().getTime();
    return now - data.savedAt > expiryTime;
  }

  /**
   * Sanitize sensitive data before saving
   */
  private sanitizeData(data: OrderFormProgressData): OrderFormProgressData {
    const sanitized: OrderFormProgressData = {};

    // Sanitize shipping_address
    if (data.shipping_address) {
      sanitized.shipping_address = this.sanitizeFormData(data.shipping_address);
    }

    // Sanitize billing_address
    if (data.billing_address) {
      sanitized.billing_address = this.sanitizeFormData(data.billing_address);
    }

    // has_billing_address is safe to save
    if (data.has_billing_address !== undefined) {
      sanitized.has_billing_address = data.has_billing_address;
    }

    return sanitized;
  }

  /**
   * Remove excluded fields from form data object
   */
  private sanitizeFormData(formData: CustomFormData): CustomFormData {
    const sanitized: CustomFormData = {};

    for (const [key, value] of Object.entries(formData)) {
      // Check if key contains any of the excluded fields
      const isExcluded = this.EXCLUDED_FIELDS.some((excludedField) =>
        key.toLowerCase().includes(excludedField.toLowerCase())
      );

      if (!isExcluded) {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Check if localStorage exists and is available
   */
  isLocalStorageAvailable(): boolean {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }

    try {
      const testKey = '__test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }
}
